/**
 * Blog Category Tabs Component (Responsive — Mobile First)
 *
 * A responsive tabbed navigation component for filtering blog posts by category.
 * Features horizontal scrolling on desktop with navigation arrows and touch-friendly
 * scrolling on mobile devices. Includes smooth animations and gradient styling for
 * active states.
 *
 * @example
 * ```tsx
 * <BlogCategoryTabs />
 * ```
 *
 * @returns {JSX.Element} The rendered category tabs component
 */
'use client';

import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/Button';

/**
 * Available blog categories with their metadata
 *
 * @type {Array<{label: string, count: number | null, customWidth: boolean}>}
 */
const categories = [
  { label: 'All Articles', count: null, customWidth: false },
  { label: 'AI & Automation', count: 24, customWidth: false },
  { label: 'Big data & Analytics', count: 18, customWidth: false },
  { label: 'Market Growth', count: 15, customWidth: false },
  { label: 'Company News', count: 12, customWidth: false },
  { label: 'Infrastructure', count: null, customWidth: true },
];

export default function BlogCategoryTabs() {
  /**
   * Currently selected category label
   * @type {[string, React.Dispatch<React.SetStateAction<string>>]}
   */
  const [activeCategory, setActiveCategory] = useState('All Articles');

  /**
   * Whether to show the left navigation arrow (desktop only)
   * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
   */
  const [showLeftArrow, setShowLeftArrow] = useState(false);

  /**
   * Whether to show the right navigation arrow (desktop only)
   * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
   */
  const [showRightArrow, setShowRightArrow] = useState(true);

  /**
   * Reference to the scrollable categories container
   * @type {React.MutableRefObject<HTMLDivElement | null>}
   */
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Scrolls the categories container horizontally
   *
   * @param {'left' | 'right'} direction - The direction to scroll
   * @returns {void}
   *
   * @example
   * ```tsx
   * scroll('left');  // Scrolls 200px to the left
   * scroll('right'); // Scrolls 200px to the right
   * ```
   */
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === 'right' ? scrollAmount : -scrollAmount);

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });

      setTimeout(updateArrowVisibility, 300);
    }
  };

  /**
   * Updates the visibility state of navigation arrows based on scroll position
   *
   * Calculates whether to show left/right arrows by comparing the current scroll position
   * with the container dimensions. Adds a 10px buffer for better UX.
   *
   * @returns {void}
   *
   * @example
   * ```tsx
   * // Called automatically after scrolling or manually when needed
   * updateArrowVisibility();
   * ```
   */
  const updateArrowVisibility = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  /**
   * Event handler for scroll events on the categories container
   *
   * Debounced scroll handler that updates arrow visibility when user scrolls
   * through categories. Provides visual feedback for navigation state.
   *
   * @returns {void}
   *
   * @example
   * ```tsx
   * // Used as onScroll handler
   * <div onScroll={handleScroll}>...</div>
   * ```
   */
  const handleScroll = () => updateArrowVisibility();

  return (
    <div className="flex items-center gap-2 md:gap-4 mb-6 md:mb-12 relative animate-fade-in">
      {/* Left Arrow - hidden on small screens */}
      {showLeftArrow && (
        <div className="hidden md:flex items-center relative">
          <div className="absolute w-[208px] h-[136px] flex-shrink-0 bg-white blur-[35px] -left-[100px] -z-10" />
          <button
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center relative z-10"
            onClick={() => scroll('left')}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 34C9.1632 34 2 26.8368 2 18C2 9.1632 9.1632 2 18 2C26.8368 2 34 9.1632 34 18C34 26.8368 26.8368 34 18 34Z"
                fill="black"
                stroke="black"
                strokeWidth="3.2"
                strokeLinejoin="round"
              />
              <path
                d="M20.3997 10.8L13.1997 18L20.3997 25.2"
                stroke="white"
                strokeWidth="3.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Scrollable Categories */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="
          flex items-center 
          gap-2 md:gap-4 
          overflow-x-auto 
          scrollbar-hide 
          flex-1
          snap-x snap-mandatory
        "
      >
        {categories.map((category, index) => (
          <Button
            key={index}
            variant={activeCategory === category.label ? 'default' : 'outline'}
            onClick={() => setActiveCategory(category.label)}
            className={`
              flex-shrink-0
              whitespace-nowrap
              text-[14px] sm:text-[16px] md:text-[18px]
              h-[42px] sm:h-[48px] md:h-[54px]
              px-4 sm:px-6 md:px-8
              rounded-[6px] md:rounded-[8px]
              font-manrope font-medium
              transition-all
              snap-start
              ${category.customWidth ? 'w-auto md:w-[223px]' : 'w-auto md:flex-1'}
              ${
                activeCategory === category.label
                  ? 'gradient-bg text-white hover:opacity-90'
                  : 'bg-white text-androia-text border border-androia-gray-200 hover:bg-androia-gray-50'
              }
            `}
          >
            {category.label}
            {category.count && (
              <span className="ml-1 text-sm md:text-base">({category.count})</span>
            )}
          </Button>
        ))}
      </div>

      {/* Right Arrow - hidden on small screens */}
      {showRightArrow && (
        <div className="hidden md:flex items-center relative">
          <div className="absolute w-[208px] h-[136px] flex-shrink-0 bg-white blur-[35px] -right-[100px] -z-10" />
          <button
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center relative z-10"
            onClick={() => scroll('right')}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 34C9.1632 34 2 26.8368 2 18C2 9.1632 9.1632 2 18 2C26.8368 2 34 9.1632 34 18C34 26.8368 26.8368 34 18 34Z"
                fill="black"
                stroke="black"
                strokeWidth="3.2"
                strokeLinejoin="round"
              />
              <path
                d="M15.6003 25.2L22.8003 18L15.6003 10.8"
                stroke="white"
                strokeWidth="3.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
